// src/components/ProfilePage.jsx
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import PageWrapper from './PageWrapper';
import axiosInstance from '../axiosInstance';
import { UserIcon, MailIcon, PhoneCallIcon, KeyIcon, CopyIcon } from '../common/Icons';
import { LanguageContext } from '../contexts/LanguageContext'; // Import LanguageContext
import { ToastContainer, toast } from 'react-toastify'; // Import ToastContainer and toast

// Translations object, moved outside the component to prevent re-creation on every render
const translations = {
  en: {
    myProfile: "My Profile",
    loadingProfile: "Loading profile...",
    failedToFetchUser: "Failed to fetch user data.",
    sessionExpired: "Your session has expired or is invalid. Redirecting to login...",
    networkError: "Network or server error.",
    error: "Error",
    goToLogin: "Go to Login",
    fullName: "Full Name",
    phoneNumber: "Phone Number",
    email: "Email",
    referralCode: "Referral Code",
    copied: "Copied!",
    saveChanges: "Save Changes",
    cancel: "Cancel",
    editProfile: "Edit Profile",
    noChanges: "No changes to save.",
    profileUpdatedSuccess: "Profile updated successfully!",
    failedToUpdateProfile: "Failed to update profile.",
    copyToClipboard: "Copy to clipboard",
    copyFailed: "Failed to copy!", // Added for consistency
    copyUnsupported: "Copy not supported on this browser." // Added for consistency
  },
  am: {
    myProfile: "የእኔ መገለጫ",
    loadingProfile: "መገለጫ በመጫን ላይ...",
    failedToFetchUser: "የተጠቃሚ ውሂብ ማግኘት አልተሳካም።",
    sessionExpired: "የእርስዎ ክፍለ ጊዜ አልቋል ወይም ልክ ያልሆነ ነው። ወደ መግቢያ በመዞር ላይ...",
    networkError: "የአውታረ መረብ ወይም የአገልጋይ ስህተት።",
    error: "ስህተት",
    goToLogin: "ወደ መግቢያ ይሂዱ",
    fullName: "ሙሉ ስም",
    phoneNumber: "ስልክ ቁጥር",
    email: "ኢሜይል",
    referralCode: "ሪፈራል ኮድ",
    copied: "ተቀድቷል!",
    saveChanges: "ለውጦችን አስቀምጥ",
    cancel: "ሰርዝ",
    editProfile: "መገለጫ አርትዕ",
    noChanges: "ለማስቀመጥ ምንም ለውጦች የሉም።",
    profileUpdatedSuccess: "መገለጫ በተሳካ ሁኔታ ተዘምኗል!",
    failedToUpdateProfile: "መገለጫ ማዘመን አልተሳካም።",
    copyToClipboard: "ወደ ቅንጥብ ሰሌዳ ቅዳ",
    copyFailed: "መቅዳት አልተቻለም!",
    copyUnsupported: "ቅጂ በዚህ አሳሽ አይደገፍም።"
  },
  or: {
    myProfile: "Profaayilii Koo",
    loadingProfile: "Profaayilii fe'amaa jira...",
    failedToFetchUser: "Odeeffannoo fayyadamaa argachuu hindandeenye.",
    sessionExpired: "Sesshiniin keessan xumureera ykn hin hojjetu. Gara seensaatti deebi'aa...",
    networkError: "Dogoggora networkii ykn serverii.",
    error: "Dogoggora",
    goToLogin: "Gara seensaatti deebi'i",
    fullName: "Maqaa Guutuu",
    phoneNumber: "Lakkoofsa Bilbilaa",
    email: "Email",
    referralCode: "Koodii Ergaa",
    copied: "Copia gochuu!",
    saveChanges: "Jijjiirama Quusadhu",
    cancel: "Dhiisi",
    editProfile: "Profaayilii jijjiiri",
    noChanges: "Jijjiiramni qusachuuf hin jiru.",
    profileUpdatedSuccess: "Profaayiliin milkaa'inaan haaromfameera!",
    failedToUpdateProfile: "Profaayilii haaromsuu hin milkoofne.",
    copyToClipboard: "Gara clipboarditti maxxansi",
    copyFailed: "Copia gochuu hin dandeenye!",
    copyUnsupported: "Copia gochuun braawzara kana irratti hin deeggaramu."
  },
  ti: {
    myProfile: "ናይ ብሕተይ ፕሮፋይል",
    loadingProfile: "ፕሮፋይል ይጽዕን ኣሎ...",
    failedToFetchUser: "ናይ ተጠቃሚ ሓበሬታ ምምጻእ ኣይከኣለን።",
    sessionExpired: "ናይ'ዚ ስራሕ ግዜኹም ወዲኡ ወይ ድማ ቅኑዕ ኣይኮነን። ናብ ምእታው ይምለስ ኣሎ...",
    networkError: "ጌጋ መርበብ ወይ ሰርቨር።",
    error: "ጌጋ",
    goToLogin: "ናብ ምእታው ይኸይድ",
    rank: "ደረጃ",
    playerName: "ሽም ተጻዋታይ",
    totalWinnings: "ጠቕላላ ዓወት (ETB)"
  },
  wo: {
    myProfile: "Profaayilii Koo",
    loadingProfile: "Profaayilii fe'amaa jira...",
    failedToFetchUser: "Odeeffannoo fayyadamaa argachuu hindandeenye.",
    sessionExpired: "Sesshiniin keessan xumureera ykn hin hojjetu. Gara seensaatti deebi'aa...",
    networkError: "Dogoggora networkii ykn serverii.",
    error: "Dogoggora",
    goToLogin: "Gara seensaatti deebi'i",
    rank: "Sadarkaa",
    playerName: "Maqaa Taphataa",
    totalWinnings: "Injifannoo Waliigalaa (ETB)"
  }
};

/**
 * ProfilePage component allows users to view and edit their profile information.
 * It fetches user data, handles updates, and provides a referral code copy feature.
 * It supports internationalization and robust error handling.
 */
const ProfilePage = ({ setCurrentPage, ...props }) => { // Destructure setCurrentPage
  const { language } = useContext(LanguageContext); // Use LanguageContext to get current language
  const navigate = useNavigate(); // Hook for navigation
  const [userData, setUserData] = useState({ // State to hold user profile data
    fullName: '',
    phoneNumber: '',
    email: '',
    referralCode: '',
    depositBalance: 0,
    bonusBalance: 0
  });
  const [loading, setLoading] = useState(true); // Loading state for data fetching
  const [error, setError] = useState(''); // State for critical page-level errors
  const [isEditing, setIsEditing] = useState(false); // State to control edit mode
  const [originalUserData, setOriginalUserData] = useState(null); // Stores data before editing for cancellation
  const [formChanged, setFormChanged] = useState(false); // Tracks if form data has changed

  // Memoized translation helper function to prevent unnecessary re-renders
  const t = useCallback((key, variables = {}) => {
    let translation = translations[language]?.[key] || translations.en[key] || key;
    for (const [placeholder, value] of Object.entries(variables)) {
      translation = translation.replace(`{${placeholder}}`, value);
    }
    return translation;
  }, [language]); // Removed 'translations' from dependency array as it's now stable

  // Toast utility function for displaying notifications
  const showToast = useCallback((message, type = 'error') => {
    toast[type](message, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  }, []);

  // Callback to fetch user profile data from the backend API
  const fetchUserProfile = useCallback(async () => {
    try {
      setLoading(true); // Set loading state to true before API call
      const response = await axiosInstance.get('/user/profile'); // Make API call to get user profile
      if (response.data.success) {
        setUserData({
          ...response.data.user,
          // Ensure balance values are parsed as floats and default to 0.00 if invalid
          depositBalance: parseFloat(response.data.user.depositBalance) || 0.00,
          bonusBalance: parseFloat(response.data.user.bonusBalance) || 0.00,
        });
        setError(''); // Clear any previous errors on successful fetch
      } else {
        // Use toast for non-critical errors, keep setError for page-level errors
        showToast(response.data.message || t('failedToFetchUser'), 'error');
        setError(t('error')); // Still set a generic error for the page
      }
    } catch (err) {
      console.error('Error fetching user profile:', err); // Log error for debugging purposes in production
      // Handle specific error types for better user feedback and redirection
      if (err.response?.status === 401 || err.response?.status === 403) {
        showToast(t('sessionExpired'), 'error');
        setError(t('sessionExpired'));
        localStorage.removeItem('token'); // Clear token on session expiration
        navigate('/login'); // Redirect to login page
      } else if (!err.response) {
        // Handle network errors (no response from server)
        showToast(t('networkError'), 'error');
        setError(t('networkError'));
        localStorage.removeItem('token'); // Clear token in case of network issues affecting auth
        navigate('/login'); // Redirect to login page
      } else {
        // Handle other API errors
        showToast(err.response?.data?.message || t('fetchError'), 'error');
        setError(err.response?.data?.message || t('fetchError'));
      }
    } finally {
      setLoading(false); // Always set loading to false after fetch attempt
    }
  }, [navigate, t, showToast]); // Dependencies for useCallback

  // Effect hook to fetch user profile data on component mount and set the current page title
  useEffect(() => {
    setCurrentPage(t('myProfile')); // Set current page title using translation
    fetchUserProfile(); // Call the fetch function
  }, [setCurrentPage, fetchUserProfile, t]); // Removed 'props' from dependencies

  // Handler for input field changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setUserData(prev => {
      const newData = { ...prev, [name]: value };
      // Check if form data has changed compared to original data
      if (originalUserData && (
        newData.fullName !== originalUserData.fullName ||
        newData.email !== originalUserData.email
      )) {
        setFormChanged(true);
      } else {
        setFormChanged(false);
      }
      return newData;
    });
  }, [originalUserData]); // Dependency on originalUserData to compare changes

  // Handler for entering edit mode
  const handleEditClick = useCallback(() => {
    setOriginalUserData(userData); // Save current data as original for potential cancellation
    setIsEditing(true); // Enable edit mode
    setFormChanged(false); // Reset form changed status
  }, [userData]); // Dependency on userData to save its current state

  // Handler for submitting profile changes
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault(); // Prevent default form submission behavior

    if (!formChanged) {
      showToast(t('noChanges'), 'info'); // Inform user if no changes to save
      setIsEditing(false); // Exit edit mode
      return;
    }

    try {
      // API call to update user profile
      const response = await axiosInstance.put('/user/profile', {
        fullName: userData.fullName,
        email: userData.email
      });

      if (response.data.success) {
        showToast(t('profileUpdatedSuccess'), 'success'); // Show success toast
        setIsEditing(false); // Exit edit mode
        fetchUserProfile(); // Re-fetch profile to ensure data is up-to-date
      } else {
        showToast(response.data.message || t('failedToUpdateProfile'), 'error'); // Show error toast
      }
    } catch (err) {
      console.error('Profile update error:', err); // Log error for debugging
      showToast(err.response?.data?.message || t('networkError'), 'error'); // Show network/API error toast
    }
  }, [formChanged, userData.fullName, userData.email, fetchUserProfile, t, showToast]); // Dependencies for useCallback

  // Handler for cancelling edits
  const handleCancel = useCallback(() => {
    if (originalUserData) {
      setUserData(originalUserData); // Revert to original data
    }
    setIsEditing(false); // Exit edit mode
    setFormChanged(false); // Reset form changed status
  }, [originalUserData]); // Dependency on originalUserData to revert changes

  // Handler for keydown events (e.g., preventing Enter key submission during edit)
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && isEditing) {
      e.preventDefault(); // Prevent default form submission on Enter key
    }
  }, [isEditing]); // Dependency on isEditing

  // Function to copy referral code to clipboard
  const copyReferralCode = useCallback(() => {
    if (userData.referralCode) {
      // Use document.execCommand('copy') for better compatibility in iframes
      const textArea = document.createElement('textarea');
      textArea.value = userData.referralCode;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          showToast(t('copied'), 'info'); // Show success toast
        } else {
          showToast(t('copyFailed'), 'error'); // Show error toast if copy failed
        }
      } catch (err) {
        console.error('Failed to copy text:', err); // Log error for debugging
        showToast(t('copyUnsupported'), 'error'); // Show error if clipboard API is unsupported
      }
      document.body.removeChild(textArea); // Clean up the temporary textarea
    }
  }, [userData.referralCode, t, showToast]); // Dependencies for useCallback

  // Function to format referral code (e.g., pad with leading zeros)
  const formatReferralCode = useCallback((code) => {
    if (!code) return 'N/A';
    // Ensure code is treated as string, pad with leading zeros to make 6 digits
    return String(code).padStart(6, '0');
  }, []); // No dependencies as it's a pure function

  // Render loading state while data is being fetched
  if (loading) {
    return (
      <PageWrapper {...props}>
        <div className="flex justify-center items-center h-screen">
          <p className="text-gray-700 text-lg">{t('loadingProfile')}</p>
        </div>
      </PageWrapper>
    );
  }

  // Render a critical error page if session expired or network error occurred
  if (error && (error === t('sessionExpired') || error === t('networkError'))) {
    return (
      <PageWrapper {...props}>
        <div className="flex flex-col justify-center items-center h-screen text-red-600">
          <p className="text-lg mb-2">{t('error')}: {error}</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md"
          >
            {t('goToLogin')}
          </button>
        </div>
      </PageWrapper>
    );
  }

  // Main profile display content
  return (
    <PageWrapper {...props} backgroundClass="bg-gradient-to-br from-indigo-900 to-purple-800 text-white min-h-screen">
      <ToastContainer /> {/* ToastContainer for displaying notifications */}
      <div className="relative w-full px-4 py-8 mx-auto sm:max-w-xl md:max-w-2xl lg:max-w-4xl xl:max-w-6xl">
        {/* Page title with an icon */}
        <h1 className="text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-8 flex items-center justify-center space-x-3">
          <UserIcon className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400" />
          <span>{t('myProfile')}</span>
        </h1>

        {/* Profile details form */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-white border-opacity-20">
          <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="space-y-3 sm:space-y-4">
            {/* Full Name field */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              <UserIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-300" />
              <div className="flex-1">
                <label htmlFor="fullName" className="block text-xs sm:text-sm font-medium text-gray-300">{t('fullName')}</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={userData.fullName}
                  onChange={handleChange}
                  readOnly={!isEditing} // Read-only unless in edit mode
                  className={`mt-1 block w-full p-2 text-sm sm:text-base rounded-md bg-gray-700 text-white ${
                    isEditing ? 'focus:ring-indigo-500 focus:border-indigo-500 border-gray-500' : 'bg-opacity-50 cursor-not-allowed'
                  }`}
                />
              </div>
            </div>

            {/* Phone Number field (read-only) */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              <PhoneCallIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-300" />
              <div className="flex-1">
                <label htmlFor="phoneNumber" className="block text-xs sm:text-sm font-medium text-gray-300">{t('phoneNumber')}</label>
                <input
                  type="text"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={userData.phoneNumber}
                  readOnly // Phone number is read-only
                  className="mt-1 block w-full p-2 text-sm sm:text-base rounded-md bg-gray-700 text-white bg-opacity-50 cursor-not-allowed"
                />
              </div>
            </div>

            {/* Email field */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              <MailIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-300" />
              <div className="flex-1">
                <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-300">{t('email')}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={userData.email || ''} // Handle null/undefined email
                  onChange={handleChange}
                  readOnly={!isEditing} // Read-only unless in edit mode
                  className={`mt-1 block w-full p-2 text-sm sm:text-base rounded-md bg-gray-700 text-white ${
                    isEditing ? 'focus:ring-indigo-500 focus:border-indigo-500 border-gray-500' : 'bg-opacity-50 cursor-not-allowed'
                  }`}
                />
              </div>
            </div>

            {/* Referral Code field with copy button */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              <KeyIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-300" />
              <div className="flex-1 relative">
                <label htmlFor="referralCode" className="block text-xs sm:text-sm font-medium text-gray-300">{t('referralCode')}</label>
                <div className="flex items-center mt-1">
                  <input
                    type="text"
                    id="referralCode"
                    name="referralCode"
                    value={formatReferralCode(userData.referralCode)} // Display formatted referral code
                    readOnly // Referral code is read-only
                    className="block w-full p-2 text-sm sm:text-base rounded-md bg-gray-700 text-white bg-opacity-50 cursor-not-allowed"
                  />
                  <button
                    type="button"
                    onClick={copyReferralCode}
                    disabled={!userData.referralCode} // Disable if no referral code
                    className="ml-2 p-2 rounded-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
                    title={t('copyToClipboard')} // Tooltip for copy button
                  >
                    <CopyIcon className="w-4 h-4 sm:w-5 sm:h-5" /> {/* Copy icon */}
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons (Save/Cancel or Edit) */}
            <div className="flex justify-center mt-4 sm:mt-6 space-x-3 sm:space-x-4">
              {isEditing ? (
                <>
                  <button
                    type="submit"
                    disabled={!formChanged} // Disable if no changes
                    className={`font-bold py-1 sm:py-2 px-4 sm:px-6 rounded-lg transition-colors shadow-lg text-sm sm:text-base ${
                      formChanged ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                    }`}
                  >
                    {t('saveChanges')}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-1 sm:py-2 px-4 sm:px-6 rounded-lg transition-colors shadow-lg text-sm sm:text-base"
                  >
                    {t('cancel')}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleEditClick}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1 sm:py-2 px-4 sm:px-6 rounded-lg transition-colors shadow-lg text-sm sm:text-base"
                >
                  {t('editProfile')}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </PageWrapper>
  );
};

export default ProfilePage;
